import HtmlParser from '@/app/common/components/CellRenderers/HtmlParser';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import { MdOutlineOpenInNew } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useLazyGetPostBySlugQuery } from '@/app/common/api/wpHelpApi';
import DrawerWrapper from '../DrawerWrapper';

const HelpDrawerPreload = () => {
  const paragraphWidths = [
    [35, 92, 88, 73],
    [28, 96, 90, 84, 61],
    [22, 89, 86, 66]
  ];

  return (
    <div className="mx-lg-4 my-3">
      <div className="mb-4">
        <div className="d-flex align-items-center gap-3 mb-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: 52,
              height: 52,
              background: 'var(--falcon-gray-200)'
            }}
          >
            <div
              className="rounded-circle"
              style={{
                width: 18,
                height: 18,
                background: 'var(--falcon-gray-400)'
              }}
            />
          </div>
          <div className="flex-1">
            <TextLoading width={32} height={12} />
            <div className="mt-2">
              <TextLoading width={58} height={10} />
            </div>
          </div>
        </div>
        <TextLoading width={48} height={18} />
      </div>

      {paragraphWidths.map((widths, sectionIndex) => (
        <div
          key={sectionIndex}
          className={`rounded-3 p-3 p-lg-4 border ${
            sectionIndex > 0 ? 'mt-3' : ''
          }`}
          style={{ borderColor: 'var(--falcon-gray-300)' }}
        >
          <TextLoading width={24} height={12} />
          {widths.map((width, lineIndex) => (
            <div key={`${sectionIndex}-${lineIndex}`} className="mt-3">
              <TextLoading width={width} height={10} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

type HelpDrawerPayload = {
  slug?: string;
  title?: ReactNode;
  tooltip?: ReactNode;
  manualData?: ReactNode;
};

type HelpDrawerState = {
  drawer: {
    key?: string;
    payload?: HelpDrawerPayload | null;
  };
};

type HelpPost = {
  content?: {
    rendered?: string;
  };
};

const HelpDrawer = () => {
  const { key, payload } = useSelector(
    (state: HelpDrawerState) => state.drawer
  );
  const { slug, title, tooltip, manualData } = payload ?? {};
  const [getPost, { isFetching, data }] = useLazyGetPostBySlugQuery();
  const posts = (Array.isArray(data) ? data : []) as HelpPost[];
  useEffect(() => {
    if (slug && !manualData) getPost(slug);
  }, [getPost, key, manualData, slug]);
  return (
    <DrawerWrapper
      show={key === 'help'}
      title={
        <div className="d-flex flex-wrap gap-3 align-items-center">
          <h6 className="m-0">
            {title ? title : ''}
            {tooltip ? <HelpTooltip html={tooltip} /> : ''}
          </h6>
          {!manualData && (
            <a
              href={`https://help.mizfa.tools/${slug}/`}
              className="btn btn-falcon-primary"
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: '13px' }}
            >
              <MdOutlineOpenInNew style={{ paddingLeft: '3px' }} size={18} />
              بازکردن بلاگ
            </a>
          )}
        </div>
      }
    >
      <div style={{ color: 'inherit' }} className="m-lg-5">
        {manualData ? (
          <>{manualData}</>
        ) : !isFetching && data ? (
          <>
            {posts.length > 0 ? (
              <HtmlParser html={posts[0]?.content?.rendered} />
            ) : (
              <div className="fs-4 w-100 text-center">راهنمایی یافت نشد.</div>
            )}
          </>
        ) : (
          <HelpDrawerPreload />
        )}
      </div>
    </DrawerWrapper>
  );
};

export default HelpDrawer;
